<template>
  <ModalForm
    v-show="visible"
    title="Edit organization"
    footer-button-label="Save"
    :footer-button-action="updateOrg"
    footer-button-class="btn-lg btn-primary"
    @close="visible = false"
  >
    <div class="form-outline mb-4">
      <label class="form-label">Name:</label>
      <input
        v-model="formData.name"
        type="text"
        class="form-control form-control-lg"
        placeholder="Organization name"
      />
    </div>

    <div class="form-outline mb-4">
      <label class="form-label">Email:</label>
      <input
        v-model="formData.email"
        type="text"
        class="form-control form-control-lg"
        placeholder="Email"
      />
    </div>

    <div class="form-outline mb-4">
      <label class="form-label">Phone number:</label>
      <input
        v-model="formData.phone"
        type="text"
        class="form-control form-control-lg"
        placeholder="Phone number"
      />
    </div>

    <div class="form-outline mb-4">
      <label class="form-label">Address:</label>
      <input
        v-model="formData.address"
        type="text"
        class="form-control form-control-lg"
        placeholder="Address"
      />
    </div>

    <div v-if="requiresSiteID" class="form-outline mb-4">
      <label class="form-label"
        >Site Id:
        <i
          id="info-icon"
          class="bi bi-info-circle ms-2"
          data-toggle="tooltip"
          title="Site id must be added before sending images for grading"
        ></i>
      </label>
      <input
        v-model="formData.site_id"
        type="text"
        class="form-control form-control-lg"
        placeholder="Site Id"
      />
    </div>

    <div class="form-outline mb-4">
      <label class="form-label">Analyst organization:</label>
      <select v-model="formData.analyst_org_id" class="form-select">
        <option value="null" disabled>- select analyst organization -</option>
        <option v-for="org in orgs" :key="org.id" :value="org.id">
          {{ org.name }}
        </option>
      </select>
    </div>

    <div v-if="allowReviewer" class="form-outline mb-4">
      <label class="form-label">Reviewer:</label>
      <select v-model="formData.reviewer_id" class="form-select">
        <option value="" disabled>- select reviewer -</option>
        <option v-for="user in reviewers" :key="user.id" :value="user.id">
          {{ user.name ? user.name : user.email }}
        </option>
      </select>
    </div>

    <div v-if="formError" class="alert alert-danger">
      {{ formError }}
    </div>
  </ModalForm>
</template>

<script setup lang="ts">
import backend from "@/backend";
import ModalForm from "@/components/ModalForm.vue";
import { AppConfig } from "@/util/AppConfig";
import { validateEmail } from "@/util/validation";
import { Tooltip } from "bootstrap";
import type { Organization, UserId } from "@/types";
import { computed, ref } from "vue";
import { getReviewers } from "@/backend/useUsersAPI";
import { setOrganizationReviewer } from "@/backend/useOrganizationsAPI";

interface OrganizationFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  analyst_org_id: number | string;
  site_id: string;
  reviewer_id?: UserId;
}

interface Props {
  org: Organization;
}

const emit = defineEmits<{ (e: "dataChange"): void }>();
const props = defineProps<Props>();

const visible = ref(false);
const formData = ref<OrganizationFormData>({
  name: "",
  email: "",
  phone: "",
  address: "",
  analyst_org_id: "",
  site_id: "",
  reviewer_id: "",
});

const formError = ref<string>("");
const orgs = ref<Organization[] | null>(null);
const requiresSiteID = computed(() => AppConfig.organizationNeedSiteID);
const allowReviewer = computed(() => AppConfig.allowReviewer);
const reviewers = ref<UserId[]>([]);

async function loadReviewers() {
  try {
    const response = await getReviewers();
    reviewers.value = response.users;
  } catch (error) {
    console.error("Failed to load reviewers:", error);
  }
}

async function updateOrg(): Promise<void> {
  let emitChange = false;
  if (!formData.value.name.trim()) {
    formError.value = "Required: organization name";
  } else if (formData.value.email && !validateEmail(formData.value.email)) {
    formError.value = "Invalid email address";
  } else if (!formData.value.analyst_org_id) {
    formError.value = "Required: analyst organization";
  } else if (AppConfig.organizationNeedSiteID && !formData.value.site_id) {
    formError.value = "Required: Site Id";
  } else {
    const toUpdateData = { ...formData.value };
    delete toUpdateData.reviewer_id;
    await backend.organizations.updateOrganization(props.org.id, toUpdateData);
    emitChange = true;
  }
  if (
    AppConfig.allowReviewer &&
    props.org?.reviewer_id !== formData.value.reviewer_id
  ) {
    await setOrganizationReviewer(props.org.id, formData.value.reviewer_id);
    emitChange = true;
  }
  if (emitChange) {
    visible.value = false;
    emit("dataChange");
  }
}

async function show(): Promise<void> {
  formError.value = "";
  orgs.value = await backend.organizations.listOrganizations();
  for (const key in formData.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (formData.value as Record<string, any>)[key] =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (props.org as Record<string, any>)[key];
  }
  visible.value = true;
}

new Tooltip(document.body, { selector: "[data-toggle='tooltip']" });
if (AppConfig.allowReviewer) {
  loadReviewers();
}
defineExpose({ show });
</script>
